import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { publicRecruitmentApi } from '../../api/publicRecruitment';
import { candidatePortalApi } from '../../api/candidatePortal';
import { useCandidateAuth } from './CandidateAuthContext';
import { CareersNav } from './CareersNav';

interface DocumentUpload { dataUri: string; filename: string; documentType: string; label: string }

/**
 * The real, simplified apply step - reached only from the Job Info
 * page's "Apply Now" button, and only ever for a signed-in candidate
 * (enforced both here and, more importantly, by applicantSessionAuth
 * on the backend). All the P.11-style detail already lives on the
 * account's saved profile; this step is just cover letter + specific
 * documents + confirm.
 */
export function VacancyApplyPage() {
  const { id } = useParams<{ id: string }>();
  const vacancyId = Number(id);
  const { account, loading } = useCandidateAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !account) navigate(`/careers/login?next=/careers/vacancies/${vacancyId}/apply`);
  }, [loading, account, navigate, vacancyId]);

  const vacancyQuery = useQuery({ queryKey: ['public-vacancy', vacancyId], queryFn: () => publicRecruitmentApi.getVacancy(vacancyId) });
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);
  const [submitted, setSubmitted] = useState<{ message: string; documentsAttached: number; documentsRejected: number } | null>(null);
  const { register, handleSubmit } = useForm<{ coverLetter: string }>();

  const applyMutation = useMutation({
    mutationFn: (values: { coverLetter: string }) => candidatePortalApi.applyWithAccount(vacancyId, { coverLetter: values.coverLetter, documents }),
    onSuccess: (result) => setSubmitted(result.data),
    onError: (err) => window.alert(err instanceof Error ? err.message : 'Failed to submit application.'),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, documentType: string, label: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setDocuments((prev) => [...prev.filter((d) => d.documentType !== documentType), { dataUri: reader.result as string, filename: file.name, documentType, label }]);
    reader.readAsDataURL(file);
  };

  if (loading || !account) return <div className="careers-page"><CareersNav /><p className="muted">Loading…</p></div>;

  if (submitted) {
    return (
      <div className="careers-page">
        <CareersNav />
        <div className="card">
          <h1>Application Submitted</h1>
          <p>{submitted.message}</p>
          {submitted.documentsRejected > 0 && <p className="muted">Note: {submitted.documentsRejected} document(s) could not be attached (check file size/type).</p>}
          <Link to="/careers/my-applications">View My Applications</Link>
        </div>
      </div>
    );
  }

  const vacancy = vacancyQuery.data?.data;

  return (
    <div className="careers-page">
      <CareersNav />
      <Link to={`/careers/vacancies/${vacancyId}`}>&larr; Back to Job Details</Link>
      {vacancy && (
        <header className="careers-header">
          <h1>Apply: {vacancy.position_title}</h1>
          <p className="muted">Applying as {account.fullName} ({account.email}). Your saved profile - education, employment history, languages, and references - is included automatically.</p>
        </header>
      )}

      <form className="card" onSubmit={handleSubmit((v) => applyMutation.mutate(v))}>
        <label>Cover Letter<textarea rows={5} {...register('coverLetter')} placeholder="Tell us why you're a good fit for this role" /></label>

        <h2>Documents</h2>
        <label>CV / Résumé (recommended)<input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleFileSelect(e, 'cv_resume', 'CV/Résumé')} /></label>
        <label>Identification Document<input type="file" accept=".pdf,image/*" onChange={(e) => handleFileSelect(e, 'identification', 'ID')} /></label>
        <label>Certificate<input type="file" accept=".pdf,image/*" onChange={(e) => handleFileSelect(e, 'certificate', 'Certificate')} /></label>
        <ul className="deadline-list">
          {documents.map((d, i) => <li key={i}>{d.label}: {d.filename}</li>)}
        </ul>

        <p className="muted">Want to update your profile details before applying? <Link to="/careers/my-profile">Edit My Profile</Link></p>

        <div className="form-actions">
          <button type="submit" disabled={applyMutation.isPending}>{applyMutation.isPending ? 'Submitting…' : 'Submit Application'}</button>
        </div>
      </form>
    </div>
  );
}
